import { create } from 'zustand';
import { supabase } from '@/lib/supabase';
import {
  fetchGroupActivities as fetchGroupActivitiesRPC,
  fetchGroupActivityById,
  createGroupActivity as createGroupActivityRPC,
  updateGroupActivity as updateGroupActivityRPC,
  cancelGroupActivity as cancelGroupActivityRPC,
  joinGroupActivity as joinGroupActivityRPC,
  leaveGroupActivity as leaveGroupActivityRPC
} from '@/lib/supabase/groupActivities';
import { GroupActivity } from '@/types/groupActivity';

interface GroupActivityState {
  groupActivities: GroupActivity[];
  selectedGroupActivity: GroupActivity | null;
  isLoading: boolean;
  error: string | null;

  fetchGroupActivities: () => Promise<void>;
  fetchGroupActivityById: (id: string) => Promise<void>;
  createGroupActivity: (activityData: Partial<GroupActivity>) => Promise<GroupActivity>;
  updateGroupActivity: (id: string, updates: Partial<GroupActivity>) => Promise<boolean>;
  cancelGroupActivity: (id: string) => Promise<boolean>;
  joinGroupActivity: (id: string) => Promise<boolean>;
  leaveGroupActivity: (id: string) => Promise<boolean>;
  clearError: () => void;
  setSelectedGroupActivity: (activity: GroupActivity | null) => void;
  inviteToGroupActivity: (activityId: string, userAuthIds: string[]) => Promise<void>;
}

export const useGroupActivityStore = create<GroupActivityState>((set, get) => ({
  groupActivities: [],
  selectedGroupActivity: null,
  isLoading: false,
  error: null,

  fetchGroupActivities: async () => {
    set({ isLoading: true, error: null });
    try {
      const activities = await fetchGroupActivitiesRPC();
      set({
        groupActivities: activities.filter(a => a.status !== 'cancelled'),
        isLoading: false
      });
    } catch (error) {
      console.error('Error in fetchGroupActivities:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch group activities',
        isLoading: false
      });
    }
  },

  fetchGroupActivityById: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      const activity = await fetchGroupActivityById(id);
      set({ selectedGroupActivity: activity, isLoading: false });
    } catch (error) {
      console.error('Error in fetchGroupActivityById:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch group activity',
        isLoading: false
      });
    }
  },

  createGroupActivity: async (activityData: Partial<GroupActivity>) => {
    set({ isLoading: true, error: null });
    try {
      const newActivity = await createGroupActivityRPC(activityData);
      set(state => ({
        groupActivities: [...state.groupActivities, newActivity],
        isLoading: false
      }));
      return newActivity;
    } catch (error) {
      console.error('Error in createGroupActivity:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to create group activity',
        isLoading: false
      });
      throw error;
    }
  },

  updateGroupActivity: async (id: string, updates: Partial<GroupActivity>) => {
    set({ isLoading: true, error: null });
    try {
      const success = await updateGroupActivityRPC(id, updates);
      if (success) {
        set(state => ({
          groupActivities: state.groupActivities.map(activity =>
            activity.id === id ? { ...activity, ...updates } : activity
          ),
          selectedGroupActivity:
            state.selectedGroupActivity?.id === id
              ? { ...state.selectedGroupActivity, ...updates }
              : state.selectedGroupActivity,
          isLoading: false
        }));
      }
      return success;
    } catch (error) {
      console.error('Error in updateGroupActivity:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to update group activity',
        isLoading: false
      });
      throw error;
    }
  },

  cancelGroupActivity: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      const success = await cancelGroupActivityRPC(id);
      if (success) {
        set(state => ({
          groupActivities: state.groupActivities.map(activity =>
            activity.id === id ? { ...activity, status: 'cancelled' } : activity
          ),
          selectedGroupActivity:
            state.selectedGroupActivity?.id === id
              ? { ...state.selectedGroupActivity, status: 'cancelled' }
              : state.selectedGroupActivity,
          isLoading: false
        }));
      }
      return success;
    } catch (error) {
      console.error('Error in cancelGroupActivity:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to cancel group activity',
        isLoading: false
      });
      throw error;
    }
  },

  joinGroupActivity: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      const success = await joinGroupActivityRPC(id);
      if (success) {
        // 1) Get current auth user
        const {
          data: { user },
          error: authError
        } = await supabase.auth.getUser();
        if (authError || !user) throw authError || new Error('Not authenticated');

        // 2) Lookup my DB profile (to get id, name, avatar)
        const { data: profile, error: profileErr } = await supabase
          .from('users')
          .select('id, full_name, avatar_url')
          .eq('auth_id', user.id)
          .single();
        if (profileErr || !profile) throw profileErr || new Error('Profile not found');

        const newParticipant = {
          id: profile.id,
          userId: profile.id,
          userName: profile.full_name,
          userAvatar: profile.avatar_url
        };

        // 3) Optimistically update store
        set(state => ({
          groupActivities: state.groupActivities.map(activity =>
            activity.id === id
              ? {
                  ...activity,
                  currentParticipants: activity.currentParticipants + 1,
                  userStatus: 'accepted',
                  participants: [...(activity.participants || []), newParticipant]
                }
              : activity
          ),
          selectedGroupActivity:
            state.selectedGroupActivity?.id === id
              ? {
                  ...state.selectedGroupActivity,
                  currentParticipants: state.selectedGroupActivity.currentParticipants + 1,
                  userStatus: 'accepted',
                  participants: [
                    ...(state.selectedGroupActivity.participants || []),
                    newParticipant
                  ]
                }
              : state.selectedGroupActivity,
          isLoading: false
        }));
      }
      return success;
    } catch (error) {
      console.error('Error in joinGroupActivity:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to join group activity',
        isLoading: false
      });
      throw error;
    }
  },

  leaveGroupActivity: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      const success = await leaveGroupActivityRPC(id);
      if (success) {
        // 1) Get current auth user
        const {
          data: { user },
          error: authError
        } = await supabase.auth.getUser();
        if (authError || !user) throw authError || new Error('Not authenticated');

        // 2) Lookup my DB profile (to get id)
        const { data: profile, error: profileErr } = await supabase
          .from('users')
          .select('id')
          .eq('auth_id', user.id)
          .single();
        if (profileErr || !profile) throw profileErr || new Error('Profile not found');

        // 3) Optimistically update store
        set(state => ({
          groupActivities: state.groupActivities.map(activity =>
            activity.id === id
              ? {
                  ...activity,
                  currentParticipants: Math.max(0, activity.currentParticipants - 1),
                  userStatus: null,
                  participants: (activity.participants || []).filter(
                    p => p.userId !== profile.id
                  )
                }
              : activity
          ),
          selectedGroupActivity:
            state.selectedGroupActivity?.id === id
              ? {
                  ...state.selectedGroupActivity,
                  currentParticipants: Math.max(
                    0,
                    state.selectedGroupActivity.currentParticipants - 1
                  ),
                  userStatus: null,
                  participants: (state.selectedGroupActivity.participants || []).filter(
                    p => p.userId !== profile.id
                  )
                }
              : state.selectedGroupActivity,
          isLoading: false
        }));
      }
      return success;
    } catch (error) {
      console.error('Error in leaveGroupActivity:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to leave group activity',
        isLoading: false
      });
      throw error;
    }
  },

  clearError: () => set({ error: null }),

  setSelectedGroupActivity: (activity: GroupActivity | null) =>
    set({ selectedGroupActivity: activity }),

  inviteToGroupActivity: async (activityId: string, userAuthIds: string[]) => {
    try {
      set({ isLoading: true, error: null });

      // 1) Get current user
      const {
        data: { user },
        error: userError
      } = await supabase.auth.getUser();
      if (userError) throw userError;
      if (!user) throw new Error('Not authenticated');

      // 2) Lookup my DB ID
      const { data: me, error: meError } = await supabase
        .from('users')
        .select('id')
        .eq('auth_id', user.id)
        .single();
      if (meError) throw meError;
      if (!me) throw new Error('User record not found');
      const myDbId = me.id;

      // 3) Verify creator or participant
      const { data: act, error: actError } = await supabase
        .from('group_activities')
        .select('creator_id')
        .eq('id', activityId)
        .single();
      if (actError) throw actError;
      if (!act) throw new Error('Activity not found');

      const { data: part } = await supabase
        .from('group_activity_participants')
        .select('id')
        .eq('group_activity_id', activityId)
        .eq('user_id', myDbId)
        .single();

      if (act.creator_id !== myDbId && !part) {
        throw new Error('Only the creator or a current participant can invite others');
      }

      // 4) Resolve invitees by auth IDs
      const { data: invitees, error: inviteesError } = await supabase
        .from('users')
        .select('id, auth_id')
        .in('auth_id', userAuthIds);
      if (inviteesError) throw inviteesError;
      if (!invitees.length) throw new Error('No matching users found to invite');

      // 5) Insert invitations using the `activity_id` column
      const invitations = invitees.map(u => ({
        activity_id: activityId,
        inviter_id:  myDbId,
        invitee_id:  u.id,
        status:      'pending'
      }));
      const { error: inviteError } = await supabase
        .from('group_activity_invitations')
        .insert(invitations);
      if (inviteError) throw inviteError;

      // 6) Refresh list
      await get().fetchGroupActivities();
    } catch (err) {
      console.error('Error inviting to group activity:', err);
      set({
        error: err instanceof Error ? err.message : 'Failed to invite members'
      });
    } finally {
      set({ isLoading: false });
    }
  }
}));
